import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Turnstile } from '@marsidev/react-turnstile'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { useAuth } from '../contexts/AuthContext'
import DubLogo from '../components/DubLogo'
import { CheckCircle, Eye, EyeOff, Users, Gift } from 'lucide-react'
import GoogleIcon from '../components/icons/GoogleIcon'
import { readIntendedUrl, clearIntendedUrl } from '../lib/intendedUrl'
import { readPendingGroupInvite } from '../lib/groupInvite'

// DUB-114: Supabase Auth → Bot Protection (Turnstile) is enabled, so every
// session-creating call needs a token — login + password reset included.
// Google OAuth bypasses Turnstile (Google verifies users separately).
const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY || ''

// Ticket c492ca99: a sign-in against an account that never clicked its
// confirmation link fails with this error forever, and nothing in the app
// could send that email a second time. Supabase reports it as the
// email_not_confirmed code; older releases only set the message.
const isEmailNotConfirmed = (err) => (
  err?.code === 'email_not_confirmed' ||
  /email not confirmed/i.test(err?.message || '')
)

export default function Login() {
  const navigate = useNavigate()
  const { signIn, signInWithGoogle, resetPassword, resendConfirmationEmail, user } = useAuth()
  const turnstileRef = useRef(null)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [rememberMe, setRememberMe] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [resetSuccess, setResetSuccess] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [captchaToken, setCaptchaToken] = useState('')
  const [unconfirmedEmail, setUnconfirmedEmail] = useState('')
  const [confirmationSent, setConfirmationSent] = useState(false)

  // Tokens are single-use — wipe + remount the widget after every attempt so
  // a retry never sends a stale token.
  const consumeCaptchaToken = () => {
    const token = captchaToken
    setCaptchaToken('')
    try { turnstileRef.current?.reset() } catch { /* widget not mounted */ }
    return token
  }

  // Navigate when user becomes authenticated
  useEffect(() => {
    if (user) {
      const intendedUrl = readIntendedUrl()
      // Always clear immediately after reading to prevent stale redirects
      clearIntendedUrl()
      // Validate the intended URL: skip admin paths if user might not have access,
      // and skip auth-related paths that would cause loops
      const skipPaths = ['/login', '/register']
      // A group invite they opened before signing up wins: /join finishes it.
      const pendingInvite = readPendingGroupInvite()
      const destination = pendingInvite
        ? `/join/${pendingInvite}`
        : (intendedUrl && !skipPaths.includes(intendedUrl))
          ? intendedUrl
          : '/home'
      navigate(destination, { replace: true })
    }
  }, [user, navigate])

  const handleLogin = async (e) => {
    e.preventDefault()
    if (TURNSTILE_SITE_KEY && !captchaToken) {
      setError('Please complete the verification challenge before continuing.')
      return
    }
    setLoading(true)
    setError('')
    setResetSuccess(false)
    setUnconfirmedEmail('')
    setConfirmationSent(false)

    try {
      await signIn(email, password, { captchaToken: consumeCaptchaToken() })
      // Redirect is handled by the useEffect that watches the user state
    } catch (err) {
      if (isEmailNotConfirmed(err)) {
        setUnconfirmedEmail(email)
        setError('Your email address hasn\'t been confirmed yet. Check your inbox for the confirmation link, or send yourself a new one below.')
      } else {
        setError(err.message || 'Failed to sign in')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleResendConfirmation = async () => {
    if (TURNSTILE_SITE_KEY && !captchaToken) {
      setError('Please complete the verification challenge before continuing.')
      return
    }

    setLoading(true)
    try {
      await resendConfirmationEmail(unconfirmedEmail, { captchaToken: consumeCaptchaToken() })
      setConfirmationSent(true)
      setError('')
    } catch (err) {
      // Supabase's own cooldown/quota messages ("you can only request this
      // after 54 seconds") are the actionable part — pass them through.
      setError(err.message || 'Failed to send confirmation email')
    } finally {
      setLoading(false)
    }
  }

  const handleForgotPassword = async () => {
    if (!email) {
      setError('Please enter your email address first')
      return
    }
    if (TURNSTILE_SITE_KEY && !captchaToken) {
      setError('Please complete the verification challenge before requesting a reset.')
      return
    }

    setLoading(true)
    setError('')
    setResetSuccess(false)
    setUnconfirmedEmail('')
    setConfirmationSent(false)

    try {
      await resetPassword(email, { captchaToken: consumeCaptchaToken() })
      setResetSuccess(true)
      setError('')
    } catch (err) {
      setError(err.message || 'Failed to send reset email')
      setResetSuccess(false)
    } finally {
      setLoading(false)
    }
  }

  const tips = [
    {
      icon: CheckCircle,
      title: "Make your picks",
      description: "Choose a tournament. Make your picks before play begins."
    },
    {
      icon: Eye,
      title: "Follow the action",
      description: "Watch the real-world event while rooting for your picks to win."
    },
    {
      icon: Users,
      title: "Compete with friends",
      description: "Create or join friendly groups and earn points to climb the leaderboard."
    },
    {
      icon: Gift,
      title: "Win cool prizes",
      description: "Top dubs get free merch!"
    }
  ]

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Welcome Section */}
      <div 
        className="hidden lg:flex lg:w-1/2 p-12 items-center justify-center relative bg-cover bg-center"
        style={{ backgroundImage: 'url(/images/backgrounds/dub-action-login-2.png)' }}
      >
        {/* Gradient overlay with 20% opacity */}
        <div className="absolute inset-0 bg-gradient-to-br from-[var(--color-royal-purple)] to-[var(--color-purple)] opacity-30"></div>
        <div className="bg-white/25 backdrop-blur-xl rounded-2xl p-12 max-w-xl w-full border border-white/30 shadow-2xl relative z-10">
          <img src="/icons/dub-logo-white.svg" alt="dub" className="h-12 mb-12" />
          <h1 className="text-5xl font-bold mb-6 text-dark-bg-heading">Go get it!</h1>
          <p className="text-xl text-dark-bg-muted mb-12">
            Win more than just bragging rights.
          </p>
          
          <div className="space-y-6">
            {tips.map((tip, index) => (
              <div key={index} className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-white/10 flex items-center justify-center">
                  <tip.icon className="h-6 w-6 text-dark-bg" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-1 text-dark-bg-heading">{tip.title}</h3>
                  <p className="text-dark-bg-muted">{tip.description}</p>
                </div>
              </div>
            ))}
          </div>
          
          <div className="text-sm text-dark-bg-muted mt-12 pt-8 border-t border-white/20">
            © 2026 dub. All rights reserved.
          </div>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-background">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden flex justify-center mb-8">
            <DubLogo className="h-12" />
          </div>

          <div className="mb-8">
            <h2 className="text-3xl font-bold mb-2">Log in</h2>
          </div>

          <form onSubmit={handleLogin} className="space-y-6">
            {error && (
              <div className="bg-destructive/10 text-destructive text-sm p-3 rounded-md">
                {error}
              </div>
            )}
            {resetSuccess && (
              <div className="bg-success/10 text-success text-sm p-3 rounded-md border border-[var(--color-success-border)]">
                Password reset email sent! Check your inbox for instructions.
              </div>
            )}
            {confirmationSent && (
              <div className="bg-success/10 text-success text-sm p-3 rounded-md border border-[var(--color-success-border)]">
                Confirmation email sent! Click the link in it, then log in.
              </div>
            )}
            {/* Ticket c492ca99: self-serve escape from an unconfirmed account —
                the only alternative was asking an admin to intervene. */}
            {unconfirmedEmail && !confirmationSent && (
              <Button
                variant="alternatePrimary"
                type="button"
                className="w-full h-12"
                onClick={handleResendConfirmation}
                disabled={loading || (TURNSTILE_SITE_KEY ? !captchaToken : false)}
              >
                {loading ? 'Sending...' : 'Resend confirmation email'}
              </Button>
            )}
            
            <div className="space-y-2">
              <label htmlFor="email" className="text-sm font-medium text-muted-foreground">
                Email
              </label>
              <Input
                id="email"
                type="email"
                name="email"
                autoComplete="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="h-12"
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="password" className="text-sm font-medium text-muted-foreground">
                Password
              </label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  autoComplete="current-password"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="h-12 pr-12"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  tabIndex={-1}
                >
                  {showPassword
                    ? <EyeOff className="h-5 w-5" />
                    : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            <div className="flex items-start space-x-2">
              <input
                type="checkbox"
                id="rememberMe"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="mt-1 h-4 w-4 rounded border-[var(--mauve-6)] text-primary focus:ring-primary"
              />
              <label htmlFor="rememberMe" className="text-sm text-muted-foreground leading-tight">
                Remember me for 30 days. Leave unchecked if you're on a shared device.
              </label>
            </div>

            {/* DUB-114: shared Turnstile widget — token covers both login and
                forgot-password. Wiped + reset after each consumption. */}
            {TURNSTILE_SITE_KEY && (
              <div className="flex justify-center">
                <Turnstile
                  ref={turnstileRef}
                  siteKey={TURNSTILE_SITE_KEY}
                  onSuccess={setCaptchaToken}
                  onError={() => setCaptchaToken('')}
                  onExpire={() => setCaptchaToken('')}
                  options={{ theme: 'light' }}
                />
              </div>
            )}

            <Button
              variant="primaryNeon"
              type="submit"
              className="w-full h-12 text-base"
              disabled={loading || (TURNSTILE_SITE_KEY ? !captchaToken : false)}
            >
              {loading ? 'Signing in...' : 'Log in now'}
            </Button>

            <div className="text-center">
              <button
                type="button"
                onClick={handleForgotPassword}
                disabled={loading || (TURNSTILE_SITE_KEY ? !captchaToken : false)}
                className="text-sm text-primary hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Forgot your password?
              </button>
            </div>
          </form>
          
          <div className="mt-8">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background px-2 text-muted-foreground">Or sign in with</span>
              </div>
            </div>
            
            <div className="grid grid-cols-1 gap-3 mt-6">
              <Button 
                variant="alternatePrimary"
                className="w-full h-12" 
                type="button"
                onClick={async () => {
                  try {
                    await signInWithGoogle()
                  } catch (err) {
                    setError(err.message || 'Failed to sign in with Google')
                  }
                }}
              >
                <GoogleIcon className="h-5 w-5 mr-2" />
                Continue with Google
              </Button>
            </div>
          </div>

          <div className="mt-8 text-center text-sm">
            Not a member yet?{' '}
            <button
              type="button"
              onClick={() => navigate('/register')}
              className="text-primary hover:underline font-semibold"
            >
              Register now
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
